import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '@/services/api';
import type { LicenseStatusResponse, LicenseSetupConfig } from '@/types';

interface LicenseContextType {
  license: LicenseStatusResponse | null;
  setupConfig: LicenseSetupConfig | null;
  isLoading: boolean;
  isLicensed: boolean;
  needsActivation: boolean;
  error: string | null;
  refreshLicenseStatus: () => Promise<void>;
  validateLicense: () => Promise<boolean>;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

export function LicenseProvider({ children }: { children: ReactNode }) {
  const [license, setLicense] = useState<LicenseStatusResponse | null>(null);
  const [setupConfig, setSetupConfig] = useState<LicenseSetupConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLicenseStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get setup config
      const configRes = await api.get('/license/setup-config');
      setSetupConfig(configRes.data);

      // Get license status
      const statusRes = await api.get('/license/status');
      setLicense(statusRes.data);
    } catch (err: any) {
      console.error('Failed to load license status:', err);
      setError(err.response?.data?.message || 'Failed to check license status');

      // Set default values on error
      setLicense({
        isLicensed: false,
        status: 'pending',
        needsActivation: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshLicenseStatus = async () => {
    await loadLicenseStatus();
  };

  const validateLicense = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return license?.isLicensed || false;
      }

      const response = await api.post('/license/validate');
      setLicense(response.data.license);
      return response.data.isValid;
    } catch (err) {
      console.error('License validation error:', err);
      return false;
    }
  };

  useEffect(() => {
    loadLicenseStatus();

    // Periodically validate license (every 1 hour)
    const interval = setInterval(() => {
      if (license?.isLicensed) {
        validateLicense();
      }
    }, 60 * 60 * 1000); // 1 hour

    return () => clearInterval(interval);
  }, []);

  const isLicensed = license?.isLicensed ?? false;
  const needsActivation = license?.needsActivation ?? true;

  return (
    <LicenseContext.Provider
      value={{
        license,
        setupConfig,
        isLoading,
        isLicensed,
        needsActivation,
        error,
        refreshLicenseStatus,
        validateLicense,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
}

// HOC to require license for a component
export function withLicenseRequired<P extends object>(
  WrappedComponent: React.ComponentType<P>
) {
  return function WithLicenseRequired(props: P) {
    const { isLicensed, needsActivation, isLoading } = useLicense();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">Checking license...</p>
          </div>
        </div>
      );
    }

    if (!isLicensed || needsActivation) {
      window.location.href = '/license-activation';
      return null;
    }

    return <WrappedComponent {...props} />;
  };
}
